import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { forwardRef, useImperativeHandle } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import "./RegistrationForm.css";
import { v4 as uuidv4 } from "uuid";
import { useFormContext } from "./RegistrationFormContext";
import moment from "moment-hijri";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";
import StepSuccessModal from "./StepSuccessModal";

const Step6Siblings = forwardRef(({ isOpen, moveToNextStep }, ref) => {
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");

  const { sharedData } = useFormContext();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    register,
    watch,
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      userId: sharedData.userId,
      siblingDtos: [
        {
          id: 0,
          name: "",
          gender: "",
          dob: null,
          isSchoolGoing: "",
          schoolName: "",
          class: "",
          mediumOfStudy: "",
        },
      ],
    },
  });

  const {
    fields: siblingDtos,
    append: appendOtherSiblingsField,
    remove: removeOtherSiblingsField,
    replace,
  } = useFieldArray({
    control,
    name: "siblingDtos",
  });

  const [dataSubmitting, setDataSubmitting] = useState(false);

  const [pupils, setPupils] = useState([]);
  const [isLoadingPupils, setIsLoadingPupils] = useState(true); // Separate loading states
  const [isLoadingSiblings, setIsLoadingSiblings] = useState(true);

  useEffect(() => {
    if (isOpen && sharedData.userId) {
      fetchPupils();
      fetchSiblings();
    }
  }, [isOpen, sharedData.userId]);

  // useEffect(() => {
  //   if (currentStep === 5 && sharedData.userId) {
  //     // Step 5 as per the index in RegistrationForm
  //     fetchPupils();
  //     fetchSiblings();
  //   }
  // }, [currentStep, sharedData.userId]);

  const fetchPupils = async () => {
    setIsLoadingPupils(true);
    try {
      console.log("Fetching pupils with userId:", sharedData.userId); // Debug
      const response = await get(
        `Student/GetPupilsInformationByUserId?userId=${sharedData.userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Fetched pupils:", response); // Debug
      setPupils(response || []);
    } catch (error) {
      console.error("Error fetching pupils:", error);
      toast.error("Failed to fetch pupils.");
    } finally {
      setIsLoadingPupils(false);
    }
  };

  const fetchSiblings = async () => {
    setIsLoadingSiblings(true);
    try {
      console.log("Fetching siblings with userId:", sharedData.userId); // Debug
      const response = await get(
        `Student/GetSiblingInformation?UserId=${sharedData.userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Fetched siblings:", response); // Debug
      if (response && response.length > 0) {
        const formattedSiblings = response.map((sibling) => ({
          id: sibling.id || 0,
          name: sibling.name || "",
          gender: sibling.gender?.toString() || "",
          dob: sibling.dob ? sibling.dob.split("T")[0] : null,
          isSchoolGoing:
            sibling.isSchoolGoing === true
              ? "Yes"
              : sibling.isSchoolGoing === false
              ? "No"
              : "",
          schoolName: sibling.schoolName || "",
          class: sibling.class || "",
          mediumOfStudy: sibling.mediumOfStudy || "",
          parentId: sibling.parentId,
        }));
        replace(formattedSiblings);
      } else {
        replace([
          {
            id: 0,
            name: "",
            gender: "",
            dob: null,
            isSchoolGoing: "",
            schoolName: "",
            class: "",
            mediumOfStudy: "",
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching siblings:", error);
      toast.error("Failed to fetch sibling information.");
    } finally {
      setIsLoadingSiblings(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: onSaveForm,
  }));

  const onSaveForm = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return false;
    }

    const isValid = await trigger("siblingDtos");
    if (!isValid) {
      return false;
    }

    setDataSubmitting(true);

    const formValues = getValues();

    // Filter out completely empty rows
    const filledSiblings = formValues.siblingDtos.filter((sibling) => {
      return (
        sibling.name ||
        sibling.gender !== "" ||
        sibling.dob ||
        sibling.isSchoolGoing !== "" ||
        sibling.schoolName ||
        sibling.class ||
        sibling.mediumOfStudy
      );
    });

    const formData = {
      userId: sharedData.userId || 0,
      siblingDtos: filledSiblings.map((sibling) => ({
        id: sibling.id || 0,
        name: sibling.name || "",
        gender:
          sibling.gender !== "" && sibling.gender !== undefined
            ? parseInt(sibling.gender)
            : null,
        dob: sibling.dob?.split("T")[0] || null,
        isSchoolGoing:
          sibling.isSchoolGoing === "Yes"
            ? true
            : sibling.isSchoolGoing === "No"
            ? false
            : null,
        schoolName: sibling.schoolName || "",
        class: sibling.class || "",
        mediumOfStudy: sibling.mediumOfStudy || "",
      })),
    };

    try {
      const response = await post("Student/AddSiblingInformation", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        setShowSuccessModal(true);
        return true;
      } else {
        toast.error(response.message || "An unexpected error occurred");
        return false;
      }
    } catch (error) {
      console.error("Error submitting sibling information:", error);
      toast.error("Failed to save sibling information.");
      return false;
    } finally {
      setDataSubmitting(false);
    }
  };

  const handleModalAddAnother = () => {
    setShowSuccessModal(false);
  };

  const handleModalNavigateToReview = () => {
    setShowSuccessModal(false);
    moveToNextStep(6);
  };

  if (isLoadingPupils || isLoadingSiblings) {
    return (
      <div className="form-step">
        <div className="text-center">
          <span className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="form-step">
      <h5 className="mb-2 p-2 px-3 text-white bg-brand rounded-pill">
        Added Pupils
      </h5>

      <div className="row">
        <div className="col-md-12">
          <table className="table custom-table table-bordered">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Gender</th>
                <th>DOB</th>
                <th>Pupil Code</th>
              </tr>
            </thead>
            <tbody>
              {pupils.map((item, index) => (
                <tr>
                  <td>{item.firstName}</td>
                  <td>{item.lastName}</td>
                  <td>{item.gender === 1 ? "Male" : "Female"}</td>
                  <td>{item.greGorianDOB.split("T")[0]}</td>
                  <td>{item.code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <h5 className="mb-3 p-2 px-3 text-white bg-brand rounded-pill">
        Other Siblings (If any)
      </h5>

      <div className="row g-2">
        <div className="col">
          <label className="form-label">Sibling Name</label>
        </div>
        <div className="col">
          <label className="form-label">Gender</label>
        </div>
        <div className="col">
          <label className="form-label">Date of Birth</label>
        </div>
        <div className="col">
          <label className="form-label">School Going</label>
        </div>
        <div className="col">
          <label className="form-label">School Name</label>
        </div>
        <div className="col">
          <label className="form-label">Class/Grade</label>
        </div>
        <div className="col">
          <label className="form-label">Medium of Study</label>
        </div>
        <div className="col-1">
          <label className="form-label">Action</label>
        </div>
      </div>

      {siblingDtos?.map((field, index) => (
        <div className="row g-2 mb-2" key={field.id}>
          <div className="col">
            <input
              type="text"
              {...register(`siblingDtos.${index}.name`, {
                validate: (value, formValues) => {
                  const sibling = getValues(`siblingDtos.${index}`);
                  const hasOtherFieldsFilled =
                    sibling.gender !== "" ||
                    sibling.dob ||
                    sibling.isSchoolGoing !== "" ||
                    sibling.schoolName ||
                    sibling.class ||
                    sibling.mediumOfStudy;

                  if (hasOtherFieldsFilled && !value?.trim()) {
                    return "This field is required.";
                  }
                  return true;
                },
              })}
              className="form-control"
            />
            {errors.siblingDtos?.[index]?.name && (
              <small className="text-danger">
                {errors.siblingDtos[index].name.message}
              </small>
            )}
          </div>
          <div className="col">
            <select
              {...register(`siblingDtos.${index}.gender`, {})}
              className="form-select"
            >
              <option value="">Select</option>
              <option value="1">Male</option>
              <option value="0">Female</option>
            </select>
            {errors.siblingDtos?.[index]?.gender && (
              <small className="text-danger">
                {errors.siblingDtos[index].gender.message}
              </small>
            )}
          </div>
          <div className="col">
            <input
              type="date"
              {...register(`siblingDtos.${index}.dob`, {})}
              className="form-control"
            />
            {errors.siblingDtos?.[index]?.dob && (
              <small className="text-danger">
                {errors.siblingDtos[index].dob.message}
              </small>
            )}
          </div>
          <div className="col">
            <select
              {...register(`siblingDtos.${index}.isSchoolGoing`, {})}
              className="form-select"
            >
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            {errors.siblingDtos?.[index]?.isSchoolGoing && (
              <small className="text-danger">
                {errors.siblingDtos[index].isSchoolGoing.message}
              </small>
            )}
          </div>
          <div className="col">
            <input
              type="text"
              {...register(`siblingDtos.${index}.schoolName`, {})}
              className="form-control"
            />
            {errors.siblingDtos?.[index]?.schoolName && (
              <small className="text-danger">
                {errors.siblingDtos[index].schoolName.message}
              </small>
            )}
          </div>
          <div className="col">
            <input
              type="text"
              {...register(`siblingDtos.${index}.class`, {})}
              className="form-control"
            />
            {errors.siblingDtos?.[index]?.class && (
              <small className="text-danger">
                {errors.siblingDtos[index].class.message}
              </small>
            )}
          </div>
          <div className="col">
            <input
              type="text"
              {...register(`siblingDtos.${index}.mediumOfStudy`, {})}
              className="form-control"
            />
            {errors.siblingDtos?.[index]?.mediumOfStudy && (
              <small className="text-danger">
                {errors.siblingDtos[index].mediumOfStudy.message}
              </small>
            )}
          </div>
          <div className="col-1">
            <button
              className="btn btn-outline-primary py-1 px-2 lh-2"
              type="button"
              onClick={() =>
                appendOtherSiblingsField({
                  id: 0,
                  name: "",
                  gender: "",
                  dob: null,
                  isSchoolGoing: "",
                  schoolName: "",
                  class: "",
                  mediumOfStudy: "",
                })
              }
            >
              <span className="fas fa-plus"></span>
            </button>
            {siblingDtos.length > 1 && (
              <button
                className="btn btn-outline-danger py-1 px-2 lh-2 ms-1"
                type="button"
                onClick={() => removeOtherSiblingsField(index)}
              >
                <span className="fas fa-minus"></span>
              </button>
            )}
          </div>
        </div>
      ))}

      <div className="mt-4">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={onSaveForm}
          disabled={dataSubmitting}
        >
          {dataSubmitting ? (
            <span
              className="spinner-border spinner-custom-size me-1"
              aria-hidden="true"
            ></span>
          ) : null}
          Save Siblings
        </button>
      </div>

      <StepSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onAddAnother={handleModalAddAnother}
        onNavigate={handleModalNavigateToReview}
        message="Siblings Saved Successfully!"
        nextButtonText="Go to Review"
      />
    </div>
  );
});

export default Step6Siblings;
